import { Http } from '@nextforce/api'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { LOCAL_BASE_URL_INFO_KEY, useServer } from 'app/server/context'
import { useAuthentication } from 'authentication/context'
import { DevToolsScreenProps } from 'home/stack/types'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Alert } from 'react-native'
import { showHeaderButton } from 'shared/header/header_button'
import { useUser } from 'users/queries/detail'
import { ApiInfo } from './types'
import { DevToolsView } from './view'

const DEFAULT_API_LIST: ApiInfo[] = [
  {
    envName: 'production',
    endpoint: 'https://napi.nextforce.us',
    response: '',
  },
  {
    envName: 'staging',
    endpoint: 'https://nstg.nextforce.us',
    response: '',
  },
  {
    envName: 'custom',
    endpoint: '',
    response: '',
  },
]

export function DevToolsScreen({ navigation }: DevToolsScreenProps) {
  const { localBaseURLInfo, onReset } = useServer()
  const { data: user } = useUser()

  const { logout } = useAuthentication()

  const [index, setIndex] = useState<number | undefined>()
  const { theme } = useTheme()

  const [apiList, setApiList] = useState<ApiInfo[]>(DEFAULT_API_LIST)

  useEffect(() => {
    if (localBaseURLInfo) {
      if (
        localBaseURLInfo.envName === 'custom' &&
        localBaseURLInfo.endpoint !== apiList[2].endpoint &&
        index === undefined
      ) {
        setApiList([
          ...apiList.slice(0, 2),
          {
            ...apiList[2],
            endpoint: localBaseURLInfo.endpoint,
          },
        ])
      }
      const currentIndex = apiList.findIndex(
        api => api.envName === localBaseURLInfo.envName
      )
      if (currentIndex !== -1 && index === undefined) {
        setIndex(currentIndex)
      }
    }
  }, [localBaseURLInfo, apiList, index])

  const onTest = useCallback(async (url: string) => {
    const isValid = isValidURL(url)
    if (isValid) {
      try {
        const info = await new Http(url).get(`/v2/actuator/info`)
        if (info) {
          return JSON.stringify(info, null, '\t') as any
        }
      } catch (err) {
        console.error(err)
        return JSON.stringify(err, null, '\t') as any
      }
    } else {
      return 'Invalid URL'
    }
  }, [])

  const onSave = useCallback(async () => {
    if (index !== undefined) {
      if (apiList[index].endpoint) {
        if (isValidURL(apiList[index].endpoint)) {
          await setBaseURLInfo(apiList[index])
          try {
            if (logout && user?.id) {
              await logout(user.id)
            }
            await onReset()
          } catch (err) {
            console.error(err)
          }
        } else {
          Alert.alert('Invalid URL')
        }
      }
    }
  }, [apiList, index, logout, onReset, user?.id])

  const onChangeText = useCallback(
    (text: string) => {
      if (index !== undefined) {
        setApiList(prevList => {
          const newList = [...prevList]
          newList[index].endpoint = text
          return newList
        })
      }
    },
    [index]
  )

  useLayoutEffect(
    () =>
      showHeaderButton({
        navigation,
        theme,
        title: t('save'),
        onPress: onSave,
      }),
    [apiList, index, navigation, onSave, theme]
  )
  return (
    <DevToolsView
      onTest={onTest}
      index={index}
      setIndex={setIndex}
      apiList={apiList}
      setApiList={setApiList}
      onChangeText={onChangeText}
    />
  )
}

const isValidURL = (url: string) => {
  const pattern = new RegExp(
    '^(http|https):\\/\\/' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?' + // port (optional)
      '(\\/[-a-z\\d%_.~+]*)*' + // path (optional)
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string (optional)
      '(\\#[-a-z\\d_]*)?$', // fragment locator (optional)
    'i'
  )
  return !!pattern.test(url)
}

const setBaseURLInfo = async (info: ApiInfo) => {
  try {
    const baseURLInfo = JSON.stringify({
      endpoint: info.endpoint,
      envName: info.envName,
    })
    await AsyncStorage.setItem(LOCAL_BASE_URL_INFO_KEY, baseURLInfo)
  } catch (e) {
    console.error(e)
  }
}
